/**
 * WHAT: Authentication and authorization guard for React routes.
 * WHY: Enforces RBAC by redirecting unauthenticated users to login and
 *      showing access-denied pages for users lacking required permissions
 *      or roles, protecting sensitive DAMA features from unauthorized access.
 */

import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

/**
 * WHAT: Route wrapper that checks authentication, permission, and role requirements.
 * WHY: Ensures only users with appropriate DAMA roles (e.g., DEV_ADMIN, ATTESTER)
 *      can access protected pages like campaign management or compliance reports.
 */
const ProtectedRoute = ({ children, requirePermission, requireRole }) => {
  const { user, loading, hasPermission, isRole } = useAuth();

  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 text-muted">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check if specific permission is required
  if (requirePermission && !hasPermission(requirePermission)) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="text-center">
          <i className="fas fa-exclamation-triangle text-warning" style={{ fontSize: '4rem' }}></i>
          <h2 className="mt-4">Access Denied</h2>
          <p className="text-muted">You don't have permission to access this page.</p>
          <a href="/" className="btn btn-primary mt-3">
            <i className="fas fa-home me-2"></i>Go to Home
          </a>
        </div>
      </div>
    );
  }

  // Check if specific role is required
  if (requireRole && !isRole(requireRole)) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="text-center">
          <i className="fas fa-exclamation-triangle text-warning" style={{ fontSize: '4rem' }}></i>
          <h2 className="mt-4">Access Denied</h2>
          <p className="text-muted">You don't have the required role to access this page.</p>
          <a href="/" className="btn btn-primary mt-3">
            <i className="fas fa-home me-2"></i>Go to Home
          </a>
        </div>
      </div>
    );
  }

  return children;
};

export default ProtectedRoute;
